import React, { useEffect, useMemo } from "react"
import { ScrollView, View } from "react-native"

import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import ConstraintsGridStep from "@common/student-diagnostic-stepper/ConstraintsGridStep"

import { SectionHeader } from "./SectionHeader"
import {
    computeAllowedDays,
    sectionStyle,
    slotStringToApi,
    stickyFooterStyle,
} from "./utils"

/**
 * Shared flow: parent flags every disponibility on the recurring weekly
 * grid so the auto-routing service has the widest possible matching
 * pool. Only days reachable before the chosen deadline are displayed.
 */
export const SharedSlotsStep = ({
    formValues,
    setFormValues,
    goToNextStep,
    user,
}) => {
    const selectedSlots = formValues?.selected_slots ?? []
    const deadlineType = formValues?.deadline_type ?? "7_days"
    const customDeadline = formValues?.custom_deadline ?? null

    const allowedDays = useMemo(
        () => computeAllowedDays(deadlineType, customDeadline),
        [deadlineType, customDeadline]
    )

    // Drop any previously-picked slot whose day fell out of the allowed
    // window when the parent bounced back to step 1 and tightened the
    // deadline. Functional updater so this only runs on actual change.
    useEffect(() => {
        if (!allowedDays) return
        setFormValues((prev) => {
            const current = prev?.selected_slots ?? []
            const kept = current.filter((slot) => {
                const day = slot.split(" ")[0]
                return allowedDays.has(day)
            })
            if (kept.length === current.length) return prev
            return { ...(prev ?? {}), selected_slots: kept }
        })
    }, [allowedDays, setFormValues])

    const slotsForApi = useMemo(
        () => selectedSlots.map(slotStringToApi).filter(Boolean),
        [selectedSlots]
    )

    const slotCountValid = slotsForApi.length >= 1

    const slotsField = useMemo(
        () => ({
            value: selectedSlots,
            onChange: (next) => {
                const incoming = Array.isArray(next) ? next : []
                setFormValues({
                    ...(formValues ?? {}),
                    selected_slots: incoming,
                })
            },
            name: "preferred_slots",
        }),
        [selectedSlots, formValues, setFormValues]
    )

    return (
        <View style={{ flex: 1, width: "100%" }}>
            <ScrollView
                style={{ width: "100%", flex: 1 }}
                contentContainerStyle={{
                    paddingHorizontal: 20,
                    paddingBottom: 12,
                }}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
            >
                <View style={sectionStyle}>
                    <SectionHeader
                        icon="calendar-week"
                        label="Créneaux préférés"
                    />
                    <TextM
                        fontSize={11}
                        color={theme.colors.grey}
                        wrap
                        style={{ marginBottom: 6, marginTop: -2 }}
                    >
                        Cochez tous les créneaux où votre enfant est disponible
                        — plus il y en a, plus vite on lui trouvera un cours.
                        Seuls les jours atteignables avant votre échéance sont
                        affichés.
                    </TextM>
                    <ConstraintsGridStep
                        field={slotsField}
                        user={user}
                        includeSunday={false}
                        compact
                        allowedDays={allowedDays}
                    />
                </View>
            </ScrollView>

            <View style={stickyFooterStyle}>
                <FilledButtonM
                    label="Continuer"
                    onPress={() => goToNextStep()}
                    disabled={!slotCountValid}
                    style={{ width: "100%" }}
                />
            </View>
        </View>
    )
}
